"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import LinkifiedText from "@/app/components/LinkifiedText";
import { plainText } from "@/lib/assistant-format";
import { streamAssistant } from "@/lib/assistant-stream";
import { assistantSessionId } from "@/lib/assistant-session";
import { useNoZoom } from "@/app/hooks/useNoZoom";

type AssistantScene = "general" | "company-request" | "article-summary" | "article-explain";

interface DialogMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  scene?: AssistantScene;
}

interface DialogContext {
  scene: AssistantScene;
  slug: string;
}

type PageKind =
  | "home"
  | "services"
  | "technologies"
  | "industries"
  | "catalog"
  | "company"
  | "article"
  | "careers"
  | "chat"
  | "other";

interface PageContext {
  kind: PageKind;
  slug: string;
}

const STORAGE_KEY = "dev48-assistant-dialog-v1";
const HINT_KEY = "dev48-assistant-hint-dismissed";
const HINT_DELAY_MS = 3000;
/** Сколько видимого текста страницы уходит в запрос; сервер режет ещё раз. */
const MAX_PAGE_TEXT = 4000;

/** Куда пользователь попал: от этого зависят стартовые чипы и приветствие. */
function detectPage(pathname: string): PageContext {
  const parts = pathname.split("/").filter(Boolean);
  const section = parts[1];
  const slug = parts[2];
  if (!section) return { kind: "home", slug: "" };
  if (section === "companies") return slug ? { kind: "company", slug } : { kind: "catalog", slug: "" };
  if (section === "articles") return slug ? { kind: "article", slug } : { kind: "other", slug: "" };
  if (section === "services") return { kind: "services", slug: "" };
  if (section === "technologies") return { kind: "technologies", slug: "" };
  if (section === "industries") return { kind: "industries", slug: "" };
  if (section === "careers") return { kind: "careers", slug: "" };
  if (section === "chat") return { kind: "chat", slug: "" };
  return { kind: "other", slug: "" };
}

/**
 * Текст страницы, которую читает пользователь: он попадает в general-сцену,
 * чтобы ассистент отвечал по тому, что видно на экране, а не по общим знаниям.
 */
function readPageText(): string | undefined {
  const node = document.getElementById("page-content");
  if (!node) return undefined;
  const text = (node.innerText ?? "").replace(/\s+/g, " ").trim();
  if (!text) return undefined;
  return text.slice(0, MAX_PAGE_TEXT);
}

/** Свободный вопрос продолжает контекст, пока он жив: статью — по статье, остальное — general. */
function followUpScene(context: DialogContext): { scene: AssistantScene; slug: string } {
  if (context.scene === "article-explain" && context.slug) {
    return { scene: "article-explain", slug: context.slug };
  }
  return { scene: "general", slug: "" };
}


function greetingFor(kind: PageKind, isEn: boolean): string {
  switch (kind) {
    case "home":
      return isEn
        ? "I advise on Dev48 technologies, services and prices — from stack to launch."
        : "Подскажу по технологиям, услугам и ценам Dev48 — от выбора стека до запуска.";
    case "services":
      return isEn
        ? "I can explain what is included, what it costs and how to start."
        : "Расскажу, что входит в услуги, сколько это стоит и как начать.";
    case "technologies":
      return isEn
        ? "Tell me the task and I will suggest a stack and explain the trade-offs."
        : "Опишите задачу — подберу стек и объясню, почему именно он.";
    case "industries":
      return isEn
        ? "I will explain what Dev48 builds for this industry."
        : "Расскажу, что Dev48 делает для этой отрасли.";
    case "catalog":
      return isEn
        ? "I will explain how the catalog works and how focus areas differ."
        : "Объясню, как устроен каталог и чем отличаются направления.";
    case "company":
      return isEn
        ? "I will tell you about this company, its news and what you can adopt from it."
        : "Расскажу о компании, её новостях и о том, что можно перенять.";
    case "article":
      return isEn ? "Ask about the article — I will explain it in plain words." : "Спросите по статье — объясню простыми словами.";
    case "careers":
      return isEn ? "I can help with open vacancies and applications." : "Расскажу про открытые вакансии и как откликнуться.";
    default:
      return isEn
        ? "Ask about technologies, services or prices — I answer from the site content."
        : "Спросите про технологии, услуги или цены — отвечу по фактам с сайта.";
  }
}

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return isMobile;
}

function GridIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" fill="none" className={className} aria-hidden="true">
      <g stroke="currentColor" strokeWidth="3">
        <line x1="16" y1="0" x2="16" y2="48" />
        <line x1="32" y1="0" x2="32" y2="48" />
        <line x1="0" y1="16" x2="48" y2="16" />
        <line x1="0" y1="32" x2="48" y2="32" />
      </g>
    </svg>
  );
}

/**
 * Плавающий ассистент: публичный помощник на всех страницах сайта.
 *
 * Место — правый нижний угол: на десктопе над переключателем языка, на
 * мобильных зеркально иконке чата. Контекст страницы определяет стартовые
 * чипы, диалог живёт в sessionStorage и не теряется при переходах.
 */
export default function AssistantWidget({ locale }: { locale: string }) {
  const isEn = locale.startsWith("en");
  const pathname = usePathname();
  const page = useMemo(() => detectPage(pathname), [pathname]);
  const isMobile = useIsMobile();

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<DialogMessage[]>([]);
  const [context, setContext] = useState<DialogContext>({ scene: "general", slug: "" });
  // Где был последний обмен: при смене типа страницы снова показываем её чипы.
  const [lastKind, setLastKind] = useState<PageKind>("other");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hydrated, setHydrated] = useState(false);
  const [hintVisible, setHintVisible] = useState(false);
  // Видимая область экрана. На iOS Safari клавиатура не сжимает layout viewport
  // (interactiveWidget из app/layout.tsx там не работает), а только visual viewport —
  // без этих данных панель на `bottom-0` уезжает под клавиатуру, а фокус в input
  // дополнительно дёргает страницу зумом.
  const [viewport, setViewport] = useState<{ top: number; height: number } | null>(null);
  const [, setInputFocused] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Пальцем окно не тянем: Telegram-стиль, зум только мешает.
  useNoZoom(open && isMobile);

  // Диалог переживает перезагрузку страницы в рамках сессии.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw) as {
          messages?: DialogMessage[];
          context?: DialogContext;
          lastKind?: PageKind;
        };
        if (Array.isArray(data.messages)) setMessages(data.messages);
        if (data.context?.scene) setContext(data.context);
        if (data.lastKind) setLastKind(data.lastKind);
      }
    } catch {
      // повреждённое состояние не должно ломать виджет
    }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages, context, lastKind }));
    } catch {
      // приватный режим — просто не сохраняем
    }
  }, [messages, context, lastKind, hydrated]);

  // Плашка первого визита: один раз, после короткой паузы.
  useEffect(() => {
    try {
      if (localStorage.getItem(HINT_KEY)) return;
    } catch {
      return;
    }
    const timer = setTimeout(() => setHintVisible(true), HINT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismissHint = useCallback(() => {
    setHintVisible(false);
    try {
      localStorage.setItem(HINT_KEY, "1");
    } catch {
      // ignore
    }
  }, []);

  // Переход по сайту сворачивает панель, но история остаётся.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (window.matchMedia("(min-width: 768px)").matches) {
      const timer = setTimeout(() => inputRef.current?.focus(), 80);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Держим шторку в границах того, что реально видно: с клавиатурой это
  // visual viewport, а не layout viewport. Тот же приём, что в ChatClient.
  useEffect(() => {
    if (!open || !isMobile) {
      setViewport(null);
      return;
    }
    const visualViewport = window.visualViewport;
    if (!visualViewport) return;
    const update = () =>
      setViewport({ top: visualViewport.offsetTop, height: visualViewport.height });
    update();
    visualViewport.addEventListener("resize", update);
    visualViewport.addEventListener("scroll", update);
    return () => {
      visualViewport.removeEventListener("resize", update);
      visualViewport.removeEventListener("scroll", update);
    };
  }, [open, isMobile]);

  // Доскролл к последнему сообщению. На мобиле — мгновенно: плавная анимация
  // дерётся с открытием клавиатуры и выглядит как рывок панели.
  useEffect(() => {
    const node = listRef.current;
    if (node) node.scrollTo({ top: node.scrollHeight, behavior: isMobile ? "auto" : "smooth" });
  }, [messages, loading, open, isMobile]);

  // Открытие/закрытие клавиатуры меняет высоту панели. Если пользователь уже
  // читал свежий ответ (лента у низа), держим его в видимой части; если
  // отлистал выше — не выдёргиваем из чтения.
  useEffect(() => {
    const node = listRef.current;
    if (!node || !viewport) return;
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
    if (distance < 120) node.scrollTop = node.scrollHeight;
  }, [viewport]);

  const send = useCallback(
    async (text: string, options?: { scene?: AssistantScene; slug?: string; display?: string }) => {
      if (loading) return;
      const message = text.trim();
      const scene = options?.scene ?? followUpScene(context).scene;
      const slug =
        options?.slug ??
        (scene === "article-summary" || scene === "article-explain" || scene === "company-request"
          ? page.slug
          : "");
      if (!message && scene !== "company-request") return;

      const display = (options?.display ?? message).trim() || (isEn ? "Draft a request" : "Составить запрос");
      const history = messages.slice(-6).map((entry) => ({ role: entry.role, content: entry.content }));
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "user", content: display },
      ]);
      setInput("");
      setError("");
      setLastKind(page.kind);
      setLoading(true);

      const assistantId = crypto.randomUUID();
      setMessages((prev) => [
        ...prev,
        { id: assistantId, role: "assistant", content: "", scene },
      ]);

      const appendChunk = (chunk: string) => {
        setMessages((prev) =>
          prev.map((message) =>
            message.id === assistantId
              ? { ...message, content: message.content + chunk }
              : message
          )
        );
      };

      try {
        const result = await streamAssistant(
          {
            scene,
            slug,
            message,
            history,
            locale,
            page: pathname,
            pageText: scene === "general" ? readPageText() : undefined,
            sessionId: assistantSessionId(),
          },
          appendChunk
        );

        const finalText = plainText(result.reply);
        setMessages((prev) =>
          prev.map((message) =>
            message.id === assistantId ? { ...message, content: finalText } : message
          )
        );

        if (scene === "article-summary" || scene === "article-explain") {
          setContext({ scene: "article-explain", slug });
        } else {
          setContext({ scene: "general", slug: "" });
        }
      } catch {
        setError(isEn ? "Could not reach the assistant" : "Не удалось получить ответ");
        setMessages((prev) => prev.filter((message) => message.id !== assistantId));
      } finally {
        setLoading(false);
      }
    },
    [loading, context, page.slug, page.kind, messages, isEn, locale, pathname]
  );

  const submit = useCallback(() => {
    const text = input.trim();
    if (!text || loading) return;
    const follow = followUpScene(context);
    void send(text, { scene: follow.scene, slug: follow.slug });
  }, [input, loading, context, send]);

  const clearDialog = useCallback(() => {
    setMessages([]);
    setContext({ scene: "general", slug: "" });
    setError("");
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Полноэкранный чат — отдельный интерфейс, виджет там не нужен.
  if (page.kind === "chat") return null;

  const greeting = greetingFor(page.kind, isEn);

  return (
    <>
      <AnimatePresence>
        {hintVisible && !open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="fixed bottom-[4.5rem] left-[calc(5vw-20px)] z-[95] flex max-w-[min(260px,78vw)] items-start gap-2 rounded-2xl border border-white/10 bg-[#202e64] px-4 py-3 shadow-2xl md:bottom-20 md:left-[calc(5vw+2rem)]"
          >
            <button
              type="button"
              onClick={() => {
                dismissHint();
                setOpen(true);
              }}
              className="text-left text-xs leading-relaxed text-white/80"
            >
              {isEn
                ? "I can advise on technologies, services and prices"
                : "Подскажу по технологиям, услугам и ценам"}
            </button>
            <button
              type="button"
              onClick={dismissHint}
              aria-label={isEn ? "Dismiss" : "Закрыть"}
              className="shrink-0 text-sm leading-none text-white/40 transition-colors hover:text-white"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => {
          dismissHint();
          setOpen((value) => !value);
        }}
        aria-label={isEn ? "Dev48 assistant" : "Ассистент Dev48"}
        aria-expanded={open}
        title={isEn ? "Dev48 assistant" : "Ассистент Dev48"}
        className="fixed bottom-6 left-[5vw] z-[95] flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full border border-white/20 bg-[#202e64] text-white/85 shadow-lg transition-all duration-200 hover:border-white/50 hover:text-white md:h-12 md:w-12"
      >
        {open ? (
          <span className="text-xl leading-none">×</span>
        ) : (
          <>
            <GridIcon className="h-5 w-5" />
            <span className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-[#2f73f2]">
              <Sparkles className="h-2.5 w-2.5 text-white" />
            </span>
          </>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={isEn ? "Dev48 assistant" : "Ассистент Dev48"}
            initial={isMobile ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            animate={isMobile ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={isMobile ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            style={
              // Мобильный ассистент — полноэкранный, как чат в Telegram: при
              // открытой клавиатуре iOS сжимает только visual viewport, поэтому
              // держим окно ровно в видимой области и верхняя кромка не едет.
              isMobile
                ? {
                    touchAction: "pan-x pan-y",
                    ...(viewport ? { top: viewport.top, height: viewport.height, bottom: "auto" } : {}),
                  }
                : undefined
            }
            className="fixed inset-x-0 top-0 z-[150] flex h-[100dvh] flex-col overflow-hidden border-white/10 bg-[#202e64] md:inset-x-auto md:bottom-28 md:left-[calc(5vw-24px)] md:top-auto md:h-[min(580px,calc(100dvh-9rem))] md:w-[380px] md:rounded-2xl md:border md:shadow-2xl"
          >
            <div
              className="flex shrink-0 items-center justify-between border-b border-white/10 px-3 pb-3 pt-[calc(env(safe-area-inset-top)+0.75rem)]"
            >
              <div className="flex items-center gap-2">
                <GridIcon className="h-4 w-4 text-white/80" />
                <span className="text-sm font-semibold text-white">
                  {isEn ? "Dev48 Assistant" : "Ассистент Dev48"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {messages.length > 0 && (
                  <button
                    type="button"
                    onClick={clearDialog}
                    className="text-xs text-white/40 transition-colors hover:text-white/80"
                  >
                    {isEn ? "Clear" : "Очистить"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label={isEn ? "Close" : "Закрыть"}
                  className="text-lg leading-none text-white/40 transition-colors hover:text-white"
                >
                  ×
                </button>
              </div>
            </div>

            <div
              ref={listRef}
              aria-live="polite"
              className="flex-1 space-y-3 overflow-y-auto overscroll-contain px-2 py-3"
            >

              {messages.map((message) =>
                message.role === "user" ? (
                  <div
                    key={message.id}
                    className="ml-auto max-w-[85%] rounded-xl border border-white/10 bg-white/[0.08] px-3 py-2 text-sm text-white/85"
                  >
                    {message.content}
                  </div>
                ) : (
                  <div key={message.id} className="max-w-[95%] text-sm leading-relaxed text-white/80">
                    <div className="whitespace-pre-wrap"><LinkifiedText text={message.content} /></div>
                  </div>
                )
              )}

              {messages.length === 0 && (
                <p className="text-sm leading-relaxed text-white/75">{greeting}</p>
              )}

              {loading && <p className="text-xs text-white/40">{isEn ? "Thinking…" : "Думаю…"}</p>}
              {error && <p className="text-sm text-red-300">{error}</p>}
            </div>

            <div className="shrink-0 border-t border-white/10 px-2 pb-2 pt-2">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") submit();
                  }}
                  onFocus={() => {
                    setInputFocused(true);
                    // Клавиатура отнимет почти пол-экрана: показываем свежий
                    // ответ, иначе он останется за нижней границей шторки.
                    if (!isMobile) return;
                    requestAnimationFrame(() => {
                      const node = listRef.current;
                      if (node) node.scrollTop = node.scrollHeight;
                    });
                  }}
                  onBlur={() => setInputFocused(false)}
                  placeholder={isEn ? "Ask a question…" : "Задайте вопрос…"}
                  enterKeyHint="send"
                  autoComplete="off"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  // 16px на мобиле: при меньшем размере iOS зумит страницу
                  // при фокусе, и шторка вместе с ней «улетает».
                  className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2 text-base text-white placeholder:text-white/40 focus:border-white/30 focus:outline-none md:text-sm"
                />

                <button
                  type="button"
                  onClick={submit}
                  disabled={loading || !input.trim()}
                  className="inline-flex shrink-0 items-center rounded-xl bg-white px-4 py-2 text-sm font-semibold text-[#202e64] transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  {isEn ? "Send" : "Спросить"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
